import React, { useEffect, useMemo } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { BadgeCheck, MapPin } from "lucide-react";
import { formatPrice, prefixUrl } from "@/lib/api";

const DEFAULT_CENTER = [22.9734, 78.6569];

export const DEFAULT_TILE_PROVIDERS = {
  map: {
    attribution: "&copy; OpenStreetMap contributors",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  },
  satellite: {
    attribution: "Tiles &copy; Esri",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  },
};

export function validCoordinate(value) {
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function pricePinIcon(marker, selected) {
  const label = marker.price ? formatPrice(marker.price) : marker.label || "View";
  const stateLabel = marker.isCurrent ? "★ " : marker.statusLabel ? `${marker.statusLabel} ` : "";
  const background = marker.isCurrent ? "#b45309" : selected ? "#0078db" : "#ffffff";
  const foreground = marker.isCurrent || selected ? "#ffffff" : "#172033";
  const border = marker.isCurrent ? "#fef3c7" : selected ? "#dbeafe" : "#d7dde7";
  const html = `<div style="position:relative;white-space:nowrap;border:2px solid ${border};background:${background};color:${foreground};border-radius:8px;padding:6px 9px;font-size:12px;font-weight:800;line-height:1;box-shadow:0 5px 16px rgba(15,23,42,.22);transform:${selected ? "scale(1.08)" : "scale(1)"};transition:transform .15s ease;">${escapeHtml(stateLabel + label)}<span style="position:absolute;left:50%;bottom:-5px;width:8px;height:8px;background:${background};border-right:2px solid ${border};border-bottom:2px solid ${border};transform:translateX(-50%) rotate(45deg);"></span></div>`;
  return L.divIcon({
    className: "shreehomes-price-pin",
    html,
    iconSize: [92, 34],
    iconAnchor: [46, 38],
    popupAnchor: [0, -38],
  });
}

function poiDivIcon(marker, selected) {
  const labels = {
    SCHOOL: "Sc", COLLEGE: "Co", UNIVERSITY: "Un", HOSPITAL: "H", METRO: "M",
    RAILWAY: "R", BUS_STOP: "B", AIRPORT: "A", MALL: "Sh", SHOPPING: "Sh",
    GROCERY: "G", MARKET: "Mk", PARK: "P", LANDMARK: "L", HIGHWAY: "Hw",
    MAJOR_ROAD: "Rd", BUSINESS_HUB: "Bh", INDUSTRIAL_AREA: "In",
    RESIDENTIAL_PROJECT: "Pr", ESSENTIALS: "E",
  };
  const label = labels[String(marker.category || "").toUpperCase()] || "POI";
  const size = selected ? 34 : 30;
  return L.divIcon({
    className: "shreehomes-poi-pin",
    html: `<div style="width:${size}px;height:${size}px;display:grid;place-items:center;border-radius:9999px;background:#0f766e;color:#fff;border:3px solid #fff;box-shadow:0 4px 12px rgba(15,23,42,.28);font-size:10px;font-weight:900;">${escapeHtml(label)}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)],
  });
}

function distanceLabel(distanceMeters) {
  const distance = Number(distanceMeters);
  if (!Number.isFinite(distance)) return "Distance unavailable";
  if (distance < 1000) return `${Math.round(distance)} m away`;
  return `${(distance / 1000).toFixed(distance < 10000 ? 1 : 0)} km away`;
}

function FitMarkers({ markers }) {
  const map = useMap();

  useEffect(() => {
    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], 14);
    } else if (markers.length > 1) {
      map.fitBounds(L.latLngBounds(markers.map((marker) => [marker.lat, marker.lng])).pad(0.2), {
        maxZoom: 15,
      });
    }
  }, [map, markers]);

  return null;
}

export default function PropertyMap({
  markers = [],
  selectedMarkerId,
  onMarkerSelect,
  mapStyle = "map",
  tileProviders = DEFAULT_TILE_PROVIDERS,
  height = 440,
  className = "",
  testId = "property-map",
}) {
  const mappableMarkers = useMemo(
    () => markers.map((marker) => ({
      ...marker,
      id: String(marker.id || marker.property_id || ""),
      lat: validCoordinate(marker.lat ?? marker.latitude),
      lng: validCoordinate(marker.lng ?? marker.longitude),
    })).filter((marker) => marker.id && marker.lat !== null && marker.lng !== null),
    [markers]
  );
  const provider = tileProviders[mapStyle] || tileProviders.map || DEFAULT_TILE_PROVIDERS.map;
  const initialCenter = mappableMarkers.length
    ? [mappableMarkers[0].lat, mappableMarkers[0].lng]
    : DEFAULT_CENTER;

  if (!mappableMarkers.length) {
    return (
      <div
        className={`grid place-items-center border border-dashed border-border bg-secondary/20 text-center ${className}`}
        style={{ height }}
        data-testid={`${testId}-empty`}
      >
        <div className="max-w-xs px-6">
          <MapPin className="mx-auto h-6 w-6 text-muted-foreground" />
          <p className="mt-2 text-sm font-semibold text-primary">Map coordinates are not available</p>
          <p className="mt-1 text-xs text-muted-foreground">The property details remain available below.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden bg-secondary/20 ${className}`} style={{ height }} data-testid={testId}>
      <MapContainer center={initialCenter} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer attribution={provider.attribution} url={provider.url} />
        <FitMarkers markers={mappableMarkers} />
        {mappableMarkers.map((marker) => {
          const selected = String(selectedMarkerId || "") === marker.id;
          const isPoi = marker.kind === "poi";
          const thumbnail = prefixUrl(marker.thumbnail_url || marker.thumbnail);
          return (
            <Marker
              key={marker.id}
              position={[marker.lat, marker.lng]}
              icon={isPoi ? poiDivIcon(marker, selected) : pricePinIcon(marker, selected)}
              zIndexOffset={marker.isCurrent ? 1000 : selected ? 500 : 0}
              eventHandlers={{ click: () => onMarkerSelect?.(marker.id) }}
            >
              <Popup>
                <div className="w-56">
                  {isPoi ? <>
                    <p className="text-[10px] font-black uppercase tracking-wider text-teal-700">{String(marker.category || "Nearby place").replaceAll("_", " ")}</p>
                    <p className="mt-1 text-sm font-bold leading-tight text-slate-950">{marker.title || "Nearby place"}</p>
                    <p className="mt-1 text-xs font-semibold text-slate-600">{distanceLabel(marker.distance_meters)}</p>
                    <p className="mt-2 text-[10px] text-slate-400">Straight-line distance · {marker.provider || "Source unavailable"}</p>
                  </> : <>
                    {thumbnail && <img src={thumbnail} alt="" className="mb-2 h-24 w-full rounded-md object-cover" loading="lazy" decoding="async" />}
                    <p className="text-sm font-bold leading-tight text-slate-950">{marker.title || "Property"}</p>
                    <p className="mt-1 text-xs text-slate-500">{[marker.locality, marker.city].filter(Boolean).join(", ")}</p>
                    <p className="mt-1 text-base font-black text-slate-950">{marker.price ? formatPrice(marker.price) : "Price on request"}</p>
                    {(marker.verified_owner || marker.verified_documents) && (
                      <p className="mt-1 flex items-center gap-1 text-[11px] font-bold text-emerald-700"><BadgeCheck className="h-3 w-3" /> Verified</p>
                    )}
                    {marker.href && <Link to={marker.href} className="mt-2 inline-flex text-xs font-bold text-[#0078db] hover:underline">View property</Link>}
                  </>}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}